// Screen data hooks. Each returns exactly one view shape from `@fsp/domain`.
//
// WHAT IS ACTUALLY TRUE HERE, as of 2026-09-17. This header used to say
// "today they compute from the simulator; later they call the API", which was
// right when every hook did one of those things and is no longer a complete
// description of the file. There are two kinds of hook in it now:
//
//   FROM THE SERVER — `useHub` and `useHubStatus`, which merge the real
//   leagues (`LeaguesProvider`, over `data/read.ts`) with the simulator's
//   example ones. The reading is not in this file; what is here is the merge.
//
//   FROM THE SIMULATOR — everything else, because the league screens past
//   setup have no endpoint behind them yet. They are synchronous and say so
//   by returning a value rather than a promise.
//
// The seam they are a binding over is the HTTP API in
// `packages/domain/src/api/` plus `Snapshot` (ICD-U01 §15), and the shape of
// these hooks is what will not have to change when a league screen moves: a
// hook keeps returning one view, and the screen keeps not knowing where it
// came from.
//
// NO RAW `SimState` LEAVES THIS FILE. `useTeams` returned `SimTeam[]` — roster
// arrays, lineup rows, FAAB budgets and the AI manager — until 2026-09-17,
// and four places outside `data/` reached through it into the simulated
// world. It returns `TeamRef[]` now, which is ICD-U01 invariant 1 and is the
// difference between a screen that can be pointed at a server and one that
// cannot.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type {
  DraftKit,
  AwardsView,
  DraftRecapView,
  DraftReplayView,
  DraftView,
  HistoryView,
  HomeView,
  HubView,
  LineupView,
  MatchupSummary,
  MatchupView,
  NewsFilter,
  NewsView,
  PlayerCardView,
  PlayerRowView,
  PlayersPage,
  WorthALookView,
  CalendarView,
  PlayersQuery,
  StatRange,
  BracketView,
  RecapView,
  RosterMoveView,
  ScheduleView,
  ScoreboardView,
  StandingsView,
  TradeBuilderView,
  TeamRef,
  TradesView,
  TrophyRoomView,
  SeasonWrapView,
  YearbookView,
  WaiversView,
  PlanView,
  CompareView,
} from '@fsp/domain';
import {
  failureOf,
  isLoading,
  valueOf,
  AwardsView as AwardsViewSchema,
  HistoryView as HistoryViewSchema,
  DraftView as DraftViewSchema,
  DraftKit as DraftKitSchema,
  NewsPage as NewsPageSchema,
  BracketView as BracketViewSchema,
  DraftRecapView as DraftRecapViewSchema,
  DraftReplayView as DraftReplayViewSchema,
  RecapView as RecapViewSchema,
  TrophyRoomView as TrophyRoomViewSchema,
  SeasonWrapView as SeasonWrapViewSchema,
  YearbookView as YearbookViewSchema,
  HomeView as HomeViewSchema,
  LineupView as LineupViewSchema,
  MatchupView as MatchupViewSchema,
  ScheduleView as ScheduleViewSchema,
  ScoreboardView as ScoreboardViewSchema,
  StandingsView as StandingsViewSchema,
  PlayerCardView as PlayerCardViewSchema,
  PlayersPage as PlayersPageSchema,
  WorthALookView as WorthALookSchema,
  CalendarView as CalendarViewSchema,
  PlanView as PlanViewSchema,
  CompareView as CompareViewSchema,
  RosterMoveView as RosterMoveViewSchema,
  WaiversView as WaiversViewSchema,
  TradesView as TradesViewSchema,
  TradeBuilderView as TradeBuilderViewSchema,
  toolsRefOf,
  withholdDraft,
  withholdHome,
  withholdHub,
  withholdLineup,
  withholdMatchup,
  withholdMove,
  withholdPlayersPage,
  withholdSchedule,
  withholdScoreboard,
  withholdTradeBuilder,
  withholdTrades,
  withholdWaivers,
  withholdWorth,
} from '@fsp/domain';
import type { Snapshot, TeamBadge } from '@fsp/domain';
import {
  awardsView,
  draftRecapView,
  draftView,
  historyView,
  homeView,
  hubView,
  lineupView,
  matchupView,
  myMatchupId,
  newsItems,
  playerNews,
  playersPage,
  worthALookView,
  calendarSimView,
  bracketView,
  recapView,
  rosterMoveView,
  scheduleView,
  scoreboardView,
  standingsView,
  teamRef,
  tradeBuilderView,
  tradesView,
  trophyRoomView,
  wrapView,
  yearbookView,
  waiversView,
  type BuilderInput,
} from '@fsp/sim';
import { mergeChores } from './chores';
import { useSession } from './session';
import { useActiveLeague, useLeagues } from './LeaguesProvider';
import { useSim } from './SimProvider';
import { leagueIdFromPath } from './route';
import { useLeagueSource, useLeagueView } from './leagueSource';
import { get } from './api';
import { refreshReads, useRead, type Read } from './read';
import { useLeagueZoneRead, type LeagueZoneRead } from './leagueZone';

/**
 * `/l/<id>` for the league on screen (or the simulator's first league outside
 * one): the prefix every league link needs.
 *
 * THE URL'S LEAGUE FIRST. This read the simulator's `state.leagueId` alone,
 * which is right in the practice world and wrong for a real league whose id
 * the simulator has never heard of: `SimProvider` falls back to its first
 * league, so every link on a league made through the form — standings rows,
 * the matchup card, "Full standings" — pointed at `/l/friends` (found
 * 2026-09-24; every seeded league shares an id with a simulated one, which is
 * why nobody saw it).
 */
export function useLeagueBase(): string {
  const { state } = useSim();
  const routeId = useRouteLeagueId();
  return `/l/${routeId ?? state.leagueId}`;
}

/**
 * The hub: every league's card and every chore.
 *
 * TWO SOURCES, and the order between them is the point. The leagues this
 * account really has come from the server (ICD-S01) and go first. The
 * simulator's example leagues follow, and only while the person has asked to
 * look around — they are labelled as examples on the screen itself (B-67).
 *
 * Until 2026-09-14 the second source was the only one, so signing in as an
 * account that owns three leagues in the database was answered with
 * "Nothing here yet".
 *
 * AND IN THE TOUR, THE TOUR'S LEAGUES. A visitor has no session and is in the
 * app only through the tutorial, so the simulated world is the tour's, and
 * it is shown. `tour` is the tour's own hub (`/demo/leagues`, the first step
 * of Game day): the tour's world and nothing else, signed in or not, because
 * its card says three leagues are there and asks for the first one, and the
 * tutorial neither shows nor disturbs a manager's own leagues (DEMO.md). Until
 * 2026-09-25 this asked only `session.demo`, which a visitor never has, so
 * that step read "Welcome, friend. Nothing here yet" under a card promising
 * three leagues — from the day the tutorial was built (995ee48).
 */
export function useHub({ tour = false }: { tour?: boolean } = {}): HubView {
  const { world, version, now, tutorial } = useSim();
  const { session } = useSession();
  const { snapshot } = useLeagues();
  const examples = tour || session?.demo === true || (!session && tutorial);
  const visible = examples ? world : [];
  const simulated = useMemo(() => withholdHub(hubView(visible, now)), [visible, version, now]);
  // `valueOf`, not `snapshot.value`: a refresh that failed keeps the leagues
  // it already had on screen rather than emptying the hub for a poll.
  const real = tour ? null : valueOf(snapshot);
  return useMemo(
    () => ({
      user: { displayName: session?.displayName ?? simulated.user.displayName },
      // THE NEWEST SEASON OF EACH LEAGUE (PLAN-2026-10-27-the-next-season
      // §5): one whose next season I am in opens from that one.
      leagues: [
        ...(real?.leagues ?? []).filter((c) => c.league.nextSeasonId === null),
        ...simulated.leagues,
      ],
      chores: mergeChores(real?.leagues.length ?? 0, real?.chores ?? null, simulated.chores),
      updatedAt: simulated.updatedAt,
    }),
    [real, simulated, session?.displayName],
  );
}

/**
 * Whether the hub is still finding out.
 *
 * Separate from `useHub` so the two screens that read the hub are not forced
 * to care: only the one that renders an empty state has to wait, because
 * "you are in no leagues" and "we have not asked yet" look identical and one
 * of them is a lie.
 */
export function useHubStatus(): {
  loading: boolean;
  /** The sentence to show when the hub could not be read. Null when it could. */
  error: string | null;
  /**
   * Whether the chore list can be believed.
   *
   * READ FROM THE PAYLOAD as of 2026-09-17. It used to be `leagues.length ===
   * 0` — a rule that lived only in this file, because the server sent `[]`
   * whether it had counted or not, and an empty chore list is not silent: it
   * renders as "All caught up. Enjoy the week." Saying that to somebody with
   * an unanswered trade offer is worse than saying nothing. `HubView.chores`
   * is nullable now and null means "nobody worked them out", so this is the
   * contract answering rather than the client guessing — and the day chores
   * are computed, the strip comes back with no change here (audit 3.7).
   */
  choresComplete: boolean;
} {
  const { snapshot } = useLeagues();
  const real = valueOf(snapshot);
  return {
    loading: isLoading(snapshot),
    // The failure's own sentence, which is written for the person: "Could not
    // reach the site" and "That league came back in a shape we do not
    // understand" are different problems and this screen is where somebody
    // finds out which one they have.
    error: failureOf(snapshot)?.message ?? null,
    choresComplete: mergeChores(real?.leagues.length ?? 0, real?.chores ?? null, []) !== null,
  };
}

/** Live views recompute at most once per second of sim time (the clock ticks once per second). */
export function useHome(): Snapshot<HomeView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const card = useActiveLeague();
  // GUARDED ON THE SOURCE. The simulator's builders throw on a real id rather
  // than return nothing, so they must not run for a real league at all.
  const simulated = useMemo(
    () => (source === 'sim' ? withholdHome(homeView(state, now)) : null),
    [source, state, version, now],
  );
  // A LEAGUE IN SETUP HAS NO HOME TO ASK FOR. It has no calendar yet, and
  // Home answers that with a 409 by design; its pages are the pre-draft ones,
  // which read the setup view. The shell's week hooks read Home on every
  // league page, so each page of a setup league asked once and failed
  // (found 2026-10-15). It stays loading: nothing a setup league draws waits
  // on it, and `useMyTeamId` takes the hub's card.
  const setup = card?.league.status === 'setup';
  return useLeagueView(
    leagueId === null || setup ? null : `/leagues/${leagueId}/home`,
    HomeViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
}

/**
 * My team in the league on screen, or null while we do not know.
 *
 * TWO WORLDS, ONE QUESTION. The simulator always knows — it owns a team
 * flagged `isMine`. A real league's answer is on the home view
 * (`league.myTeamId`), which means it can be **absent**: in flight, or a
 * league somebody is looking at without a seat in it.
 *
 * It used to be `string` with a `!` on the find, so a real league went through
 * a non-null assertion over the simulator's teams and got somebody else's id.
 */
export function useMyTeamId(): string | null {
  const { state } = useSim();
  const source = useLeagueSource();
  const home = valueOf(useHome());
  const card = useActiveLeague();
  // THE HUB'S CARD UNTIL HOME ANSWERS. The hub is what decided this league
  // is real, so it has always answered first, and its card names my team
  // too: My Team's lineup asks from that rather than waiting the length of
  // Home's read (2026-10-07). Home's, once it is here, is the newer word.
  if (source === 'api')
    return home !== null ? home.league.myTeamId : (card?.league.myTeamId ?? null);
  if (source === 'undecided') return null;
  return state.teams.find((t) => t.isMine)?.id ?? null;
}

/**
 * A team's lineup for a week: `period` for another one than now (D4), which
 * a real league's read takes as `?period=`. The simulator keeps only the
 * current week's lineup, so a practice league's screen never asks for
 * another (`LineupScreen`).
 */
export function useLineup(teamId: string | null, period?: number): Snapshot<LineupView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const home = useHome();
  const simulated = useMemo(
    () =>
      source === 'sim' && teamId !== null
        ? withholdLineup(lineupView(state, teamId, now), toolsRefOf(state.settings))
        : null,
    [source, state, version, now, teamId],
  );
  const snapshot = useLeagueView(
    leagueId === null || teamId === null
      ? null
      : `/leagues/${leagueId}/teams/${teamId}/lineup${period === undefined ? '' : `?period=${period}`}`,
    LineupViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
  // A REAL LEAGUE YOU HAVE NO TEAM IN is an answer, not a wait: Home has
  // arrived and named no team of yours. Before Home arrives it is loading
  // (U1), which `useLeagueView` now says on its own.
  if (source === 'api' && teamId === null && home.state === 'ready') {
    return {
      state: 'failed',
      failure: { code: 'not_found', message: 'You have no team in this league.' },
      lastValue: null,
      lastAsOf: null,
    };
  }
  // AND A HOME THAT FAILED IS THIS SCREEN'S FAILURE. The team id comes from
  // Home, so without this a Home that timed out left the lineup "Loading…"
  // for good, with no sentence and no Try again (B-133's review, 2026-09-24).
  if (source === 'api' && teamId === null && home.state === 'failed') {
    return { state: 'failed', failure: home.failure, lastValue: null, lastAsOf: null };
  }
  return snapshot;
}

/**
 * My games this week, in a real league that plays two opponents a week
 * (scenario Q6, B-614): Home's two for now, the schedule's for another week,
 * the first first. One game, or none, in every other league; the practice
 * league plays one opponent a week.
 */
export function useMyGames(period?: number): MatchupSummary[] {
  const source = useLeagueSource();
  const home = valueOf(useHome());
  const schedule = valueOf(useSchedule());
  if (source !== 'api') return [];
  if (period !== undefined) {
    const week = schedule?.periods.find((p) => p.period.index === period);
    return week ? [week.myMatchup, week.mySecond].filter((m) => m !== null) : [];
  }
  return home ? [home.matchup, home.secondMatchup].filter((m) => m !== null) : [];
}

export function useMyMatchupId(period?: number): string | null {
  const { state } = useSim();
  const source = useLeagueSource();
  const home = valueOf(useHome());
  const schedule = valueOf(useSchedule());
  // ANOTHER WEEK'S is the schedule's (D4, B-216): it lists my matchup for
  // every week the league plays, in either source, so no request is added.
  if (period !== undefined) {
    if (source === 'undecided') return null;
    return schedule?.periods.find((p) => p.period.index === period)?.myMatchup?.id ?? null;
  }
  // A real league's own answer, from the view that already knows it. `null`
  // here means "no game" or "not yet", and `useMatchup` treats both as
  // nothing to ask for rather than requesting a matchup called "null".
  if (source === 'api') return home?.matchup?.id ?? null;
  if (source === 'undecided') return null;
  return myMatchupId(state);
}

/**
 * One matchup.
 *
 * `null` for "no matchup to show" — a bye, or a league with no schedule — is
 * kept as a VALUE inside the snapshot rather than becoming a fourth snapshot
 * state, because "you have no game this week" is an answer and not an absence.
 *
 * THE ID CARRIES A COLON and Pages does not decode it back out of `%3A`
 * (HANDOFF, E1): encoding it is how you 404 a row that is right there. So it
 * goes into the path raw, exactly as `league:walk` sends it.
 */
/**
 * `period` says where a null id came from: Home for now, the schedule for
 * another week (`useMyMatchupId(period)`, D4). "No game" is an answer only
 * once THAT view has arrived — keyed on Home alone, a past week read as
 * "no matchup" while the schedule was still on its way.
 */
export function useMatchup(
  matchupId: string | null,
  period?: number,
): Snapshot<MatchupView | null> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const homeView = useHome();
  const scheduleView = useSchedule();
  const home = period === undefined ? homeView : scheduleView;
  const simulated = useMemo(
    () =>
      source === 'sim' && matchupId
        ? withholdMatchup(matchupView(state, matchupId, now), toolsRefOf(state.settings))
        : null,
    [source, state, version, now, matchupId],
  );
  const snapshot = useLeagueView(
    leagueId === null || matchupId === null ? null : `/leagues/${leagueId}/matchups/${matchupId}`,
    MatchupViewSchema,
    // The simulated value is nullable and the schema's output is not, so the
    // cast is here rather than at the call site: `useLeagueView` is typed to
    // the schema, and a bye is a value this hook adds on top of it.
    simulated as MatchupView,
    new Date(now).toISOString(),
  ) as Snapshot<MatchupView | null>;
  // "NO GAME THIS WEEK" IS AN ANSWER. For a real league, Home has arrived
  // and named no matchup: a ready `null`, which the screen draws as its
  // no-game state. Before Home arrives it is loading (U1). The first version
  // fell through to the practice-league sentence for both, and on a league
  // with no matchup it stayed there for good.
  if (source === 'api' && matchupId === null && home.state === 'ready') {
    return { state: 'ready', value: null, asOf: home.asOf, stale: false };
  }
  // The matchup id comes from Home too, so Home's failure is this screen's
  // (see `useLineup`).
  if (source === 'api' && matchupId === null && home.state === 'failed') {
    return { state: 'failed', failure: home.failure, lastValue: null, lastAsOf: null };
  }
  return snapshot;
}

export function useScoreboard(period?: number): Snapshot<ScoreboardView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const simulated = useMemo(
    () =>
      source === 'sim'
        ? withholdScoreboard(scoreboardView(state, now, period), toolsRefOf(state.settings))
        : null,
    [source, state, version, now, period],
  );
  return useLeagueView(
    leagueId === null
      ? null
      : `/leagues/${leagueId}/scoreboard${period === undefined ? '' : `?period=${period}`}`,
    ScoreboardViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
}

/**
 * The league the URL names, whichever world it belongs to.
 *
 * Deliberately NOT `useLeagueBase`, which answers with the SIMULATOR's league
 * id: that is the right answer for a link on a demo screen and the wrong one
 * for a request about the league somebody is actually looking at.
 */
export function useRouteLeagueId(): string | null {
  const { pathname } = useLocation();
  return useMemo(() => leagueIdFromPath(pathname), [pathname]);
}

/**
 * The table.
 *
 * FIRST OF THE SIX (BACKLOG E12) and the pattern the rest follow: compute the
 * simulated value as before, and hand it to `useLeagueView` along with the
 * path a REAL league would be read from. The decision between them is made by
 * league identity in `leagueSource.tsx` and never by which one answered.
 *
 * The return type changed from `StandingsView` to `Snapshot<StandingsView>`
 * and that is the actual cost of this story: a screen that used to be handed a
 * value now has to say what it draws while it has none, and what it draws when
 * the answer was "no". Both were always true of a real league; only the
 * simulator let them be skipped.
 */
export function useStandings(): Snapshot<StandingsView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const simulated = useMemo(
    () => (source === 'sim' ? standingsView(state, now) : null),
    [source, state, version, now],
  );
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/standings`,
    StandingsViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
}

/** The pool, as the Players screen reads it: the pages so far, and how to ask for the next. */
export interface PlayersRead {
  /** Every page read so far, as one page: the rows in order, and the last page's cursor. */
  snapshot: Snapshot<PlayersPage>;
  /** Ask for the next page. Null when there is none, or nothing to page (the simulator). */
  more: (() => void) | null;
  /** True while the next page is on its way. */
  loadingMore: boolean;
  /** Why the last ask for the next page failed, in the server's words, or null. */
  moreFailure: string | null;
}

/**
 * The four-week planner (D10, PLAN-2026-10-03-research-tools.md §8): a real
 * league's, for any team in it. The tour's simulator plays one week of pro
 * games, so it has nothing to plan and asks nothing here.
 */
export function usePlan(teamId: string | null, weeks: number): Snapshot<PlanView> {
  const leagueId = useRouteLeagueId();
  const path =
    leagueId === null || teamId === null
      ? null
      : `/leagues/${leagueId}/teams/${encodeURIComponent(teamId)}/plan?weeks=${weeks}`;
  return useLeagueView(path, PlanViewSchema, null, new Date(0).toISOString());
}

/**
 * Compare (E3, PLAN-2026-10-03-research-tools.md §9): two to four players side
 * by side in a real league. No ids asks nothing; the tour's simulator plays one
 * week, so it has no season to compare and asks nothing here either.
 */
export function useCompare(ids: readonly string[]): Snapshot<CompareView> {
  const leagueId = useRouteLeagueId();
  const path =
    leagueId === null || ids.length === 0
      ? null
      : `/leagues/${leagueId}/compare?players=${ids.map(encodeURIComponent).join(',')}`;
  return useLeagueView(path, CompareViewSchema, null, new Date(0).toISOString());
}

/** `GET /leagues/:id/players?…` for one query and, past the first page, a cursor. */
export function playersPath(leagueId: string, query: PlayersQuery, cursor?: string): string {
  const params = new URLSearchParams();
  params.set('availability', query.availability);
  // A comma list, as the route and the directory take it.
  if (query.positions.length > 0) params.set('positions', query.positions.join(','));
  if (query.slot !== undefined) params.set('slot', query.slot);
  if (query.team !== undefined) params.set('team', query.team);
  // One pro club's players (E1, B-479).
  if (query.club !== undefined) params.set('club', query.club);
  if (query.q.trim() !== '') params.set('q', query.q.trim());
  // Absent, the server's default order: points a game (B-365).
  if (query.sort !== undefined) params.set('sort', query.sort);
  if (query.reverse) params.set('reverse', 'true');
  // The stats' range and the stat a `sort=stat` orders by (B-412).
  if (query.stats !== undefined) params.set('stats', query.stats);
  if (query.stat !== undefined) params.set('stat', query.stat);
  if (query.period !== undefined) params.set('period', String(query.period));
  params.set('limit', String(query.limit));
  if (cursor !== undefined) params.set('cursor', cursor);
  return `/leagues/${leagueId}/players?${params.toString()}`;
}

/**
 * The league's player pool.
 *
 * THE SIMULATOR ANSWERS IN ONE PAGE; A REAL LEAGUE PAGES (W8). A real pool is a
 * couple of thousand players and `GET …/players` answers at most two hundred
 * at a time with a cursor bound to its filter, so `more` asks for the next
 * page and the rows are appended. The pages past the first belong to the first
 * page they followed: when the first page is read again — a new filter, or a
 * move this tab made — they are dropped rather than stitched onto a list whose
 * order may have changed underneath them.
 */
export function usePlayers(query: PlayersQuery): PlayersRead {
  const { state, version, now } = useSim();
  const source = useLeagueSource();
  const leagueId = useRouteLeagueId();
  const key = JSON.stringify(query);
  // Player rows don't need per-second recompute unless a game state flips; bucket `now` to the minute.
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    // The screens ask no order by a helper the league turned off (`useTools`).
    () =>
      source === 'sim'
        ? withholdPlayersPage(playersPage(state, query, minute * 60000), toolsRefOf(state.settings))
        : null,
    [source, state, version, minute, key],
  );
  const first = useLeagueView(
    leagueId === null ? null : playersPath(leagueId, query),
    PlayersPageSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );

  // AN ORDER BY A HELPER THE LEAGUE TURNED OFF since the hub was read
  // (RULES_SCHEMA §15c) is refused: every read on screen is asked again, the
  // hub's switches with them, and the screens ask in an order still allowed.
  const refused = first.state === 'failed' && first.failure.code === 'conflict' ? key : null;
  useEffect(() => {
    if (refused !== null) void refreshReads();
  }, [refused]);

  // The first page these pages follow: its path and the instant it answered.
  const firstValue = valueOf(first);
  const firstAsOf = first.state === 'ready' ? first.asOf : null;
  const anchor =
    source === 'api' && leagueId !== null && firstValue !== null && firstAsOf !== null
      ? `${playersPath(leagueId, query)}|${firstAsOf}`
      : null;
  const [extra, setExtra] = useState<{
    anchor: string;
    items: PlayerRowView[];
    nextCursor: string | null;
  } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailure, setMoreFailure] = useState<{ anchor: string; message: string } | null>(null);
  const live = useRef(anchor);
  live.current = anchor;
  useEffect(() => {
    // A new first page starts a new list: nothing is loading onto it yet.
    setLoadingMore(false);
  }, [anchor]);

  const mine = extra !== null && extra.anchor === anchor ? extra : null;
  const cursor = mine !== null ? mine.nextCursor : (firstValue?.nextCursor ?? null);
  const more = useCallback(() => {
    if (anchor === null || leagueId === null || cursor === null) return;
    setLoadingMore(true);
    setMoreFailure(null);
    void (async () => {
      const reply = await get(playersPath(leagueId, query, cursor), PlayersPageSchema);
      // Answering a list nobody is looking at any more.
      if (live.current !== anchor) return;
      setLoadingMore(false);
      if (reply.status !== 'ok') {
        setMoreFailure({
          anchor,
          message: reply.status === 'refused' ? reply.message : reply.failure.message,
        });
        return;
      }
      setExtra((held) => ({
        anchor,
        items: [
          ...(held !== null && held.anchor === anchor ? held.items : []),
          ...reply.data.items,
        ],
        nextCursor: reply.data.nextCursor,
      }));
    })();
    // `query` is `key`, spelled as a value.
  }, [anchor, leagueId, cursor, key]);

  const snapshot = useMemo((): Snapshot<PlayersPage> => {
    if (mine === null || first.state !== 'ready') return first;
    return {
      ...first,
      value: {
        ...first.value,
        items: [...first.value.items, ...mine.items],
        nextCursor: mine.nextCursor,
      },
    };
  }, [first, mine]);

  return {
    snapshot,
    more: source === 'api' && cursor !== null && !loadingMore ? more : null,
    loadingMore,
    moreFailure: moreFailure !== null && moreFailure.anchor === anchor ? moreFailure.message : null,
  };
}

/**
 * Worth a look's page (SCENARIOS E1, PLAN-2026-10-27-worth-a-look): a real
 * league's from `GET /worth-a-look`, the practice league's from its simulator,
 * by the same rules (`worthLists`). Its figures change as weeks end and moves
 * are made, so the simulator's is worked out to the minute, as the pool's is.
 */
export function useWorthALook(): Snapshot<WorthALookView> {
  const { state, version, now } = useSim();
  const source = useLeagueSource();
  const leagueId = useRouteLeagueId();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () =>
      source === 'sim'
        ? withholdWorth(worthALookView(state, minute * 60000), toolsRefOf(state.settings))
        : null,
    [source, state, version, minute],
  );
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/worth-a-look`,
    WorthALookSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/**
 * The calendar between two instants (scenario D12, PLAN-2026-10-30-the-
 * calendar): the days a layout draws, asked for as the reader's own
 * midnights. The practice league builds its one week from the simulator.
 */
export function useCalendar(from: number, to: number): Snapshot<CalendarView> {
  const { state, version, now } = useSim();
  const source = useLeagueSource();
  const leagueId = useRouteLeagueId();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () => (source === 'sim' ? calendarSimView(state, minute * 60000, from, to) : null),
    [source, state, version, minute, from, to],
  );
  return useLeagueView(
    leagueId === null
      ? null
      : `/leagues/${leagueId}/calendar?from=${encodeURIComponent(new Date(from).toISOString())}&to=${encodeURIComponent(new Date(to).toISOString())}`,
    CalendarViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/**
 * My roster's box score over a range (B-413): the pool's own read of `mine`
 * with its stats, for My Team's table, so the lineup's read carries none. A
 * real league's only; the tour has no box scores.
 */
export function useMyStats(range: StatRange, teamId?: string): Snapshot<PlayersPage> {
  const leagueId = useRouteLeagueId();
  // A TEAM'S PLAYERS BY ITS ID (D1, B-461), a table per position (B-514);
  // with none, mine by `mine`, which the pool reads by kind of play.
  const query: PlayersQuery = {
    availability: teamId === undefined ? 'mine' : 'all',
    ...(teamId === undefined ? {} : { team: teamId }),
    positions: [],
    q: '',
    reverse: false,
    cursor: null,
    limit: 200,
    stats: range,
  };
  return useLeagueView(
    leagueId === null ? null : playersPath(leagueId, query),
    PlayersPageSchema,
    null,
    new Date(0).toISOString(),
  );
}

/** The draft room for the active league; recomputes every sim second while live. */
export function useDraft(): DraftView {
  const { state, version, now } = useSim();
  return useMemo(() => withholdDraft(draftView(state, now)), [state, version, now]);
}

/**
 * A real league's draft: `GET /leagues/:id/draft` — the order, the seats and
 * the time before it runs, the board after (PLAN-2026-09-26-every-screen-and-
 * crayon P5). A real league drafts by itself at its time (autopick), which
 * takes seconds; the live room a practice league has is D10's.
 */
export function useLeagueDraft(): Snapshot<DraftView> {
  const leagueId = useRouteLeagueId();
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/draft`,
    DraftViewSchema,
    null,
    new Date(0).toISOString(),
  );
}

/**
 * The mock draft's kit for the league in the route (MOCK_DRAFT.md §4): one
 * read, kept for as many mocks as a visit runs. A real league's alone; the
 * practice league has no kit and asks for none.
 */
export function useMockKit(ask = true): Read<DraftKit | null> {
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  return useRead(
    ask && source === 'api' && leagueId !== null ? `/leagues/${leagueId}/draft/mock` : null,
    DraftKitSchema,
  );
}

/**
 * Every team in the active league, as the view shape.
 *
 * `TeamRef[]`, NOT `SimTeam[]`. The raw simulator team carries a roster, a
 * lineup, a FAAB balance and the AI manager holding the seat, none of which
 * is a view — and four places outside `data/` were reading through it into
 * the simulated world, which is the one thing ICD-U01 invariant 1 forbids.
 * Nothing that consumed it wanted any of that: they wanted a name, a color, a
 * record and which one is mine.
 */
export function useTeams(): TeamRef[] {
  const { state, version, now } = useSim();
  return useMemo(() => state.teams.map((t) => teamRef(state, t, now)), [state, version, now]);
}

/**
 * My team in the active league, or null.
 *
 * The three sites that reached into `SimState` for it wanted one of three
 * fields — the id, the owner's name, the secondary color — and each found it
 * a different way. There is a seat with nobody in it in every league that is
 * still filling up, so null is a real answer here.
 */
export function useMyTeam(): TeamRef | null {
  const teams = useTeams();
  return useMemo(() => teams.find((t) => t.isMine) ?? null, [teams]);
}

/**
 * The waivers screen (WAIVERS.md): my claims, the wire, the log. Recomputes per
 * sim minute; a real league reads `GET /leagues/:id/waivers` (W8).
 */
export function useWaivers(): Snapshot<WaiversView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () =>
      source === 'sim'
        ? withholdWaivers(waiversView(state, minute * 60000), toolsRefOf(state.settings))
        : null,
    [source, state, version, minute],
  );
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/waivers`,
    WaiversViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/** One of the three sheets, and the pending claim it edits. */
export interface MoveAsked {
  kind: 'add' | 'claim' | 'drop';
  playerId: string;
  claimId?: string | null;
}

/**
 * What the add, claim or drop sheet needs for one player; null when no sheet
 * is open. A real league reads `GET …/players/:playerId/move?kind=&claimId=`,
 * whose check is the move's own — a sheet that blocks nothing is a move the
 * write will take (W7).
 */
export function useMoveSheet(move: MoveAsked | null): Snapshot<RosterMoveView> | null {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const key = move ? `${move.kind}:${move.playerId}:${move.claimId ?? ''}` : '';
  const simulated = useMemo(
    () =>
      source === 'sim' && move
        ? withholdMove(
            rosterMoveView(state, move.kind, move.playerId, minute * 60000, move.claimId),
            toolsRefOf(state.settings),
          )
        : null,
    [source, state, version, minute, key],
  );
  const path =
    leagueId === null || move === null
      ? null
      : `/leagues/${leagueId}/players/${encodeURIComponent(move.playerId)}/move?kind=${move.kind}` +
        (move.claimId ? `&claimId=${encodeURIComponent(move.claimId)}` : '');
  const snapshot = useLeagueView(
    path,
    RosterMoveViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
  return move === null ? null : snapshot;
}

/**
 * One player in this league, as the card shows him: who holds him, what I can
 * do about it and why not, and his season so far (`GET …/players/:playerId`,
 * W7). A REAL LEAGUE ONLY: the simulator's card is built by whoever opens it,
 * from the row it was opened on, so this answers null there.
 */
export function usePlayerCard(playerId: string | null): Snapshot<PlayerCardView> | null {
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const snapshot = useLeagueView(
    leagueId === null || playerId === null
      ? null
      : `/leagues/${leagueId}/players/${encodeURIComponent(playerId)}`,
    PlayerCardViewSchema,
    null,
    '',
  );
  return source === 'api' && playerId !== null ? snapshot : null;
}

/**
 * The trades screen (TRADES.md): offers for me, sent, under review, history,
 * and the block. Recomputes per sim minute; a real league reads
 * `GET /leagues/:id/trades` (PLAN-2026-09-26-trades T6).
 */
export function useTrades(): Snapshot<TradesView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () => (source === 'sim' ? withholdTrades(tradesView(state, minute * 60000)) : null),
    [source, state, version, minute],
  );
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/trades`,
    TradesViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/**
 * The builder with these boxes checked: both rosters, the partners, the
 * read-out, the drops and the problem; null when closed. A real league reads
 * `GET /leagues/:id/trades/builder?with=&give=&get=&givePicks=&getPicks=&drops=&counterOf=`,
 * asked again as the boxes change.
 */
export function useTradeBuilder(input: BuilderInput | null): Snapshot<TradeBuilderView> | null {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const key = input ? JSON.stringify(input) : '';
  const simulated = useMemo(
    () =>
      source === 'sim' && input
        ? withholdTradeBuilder(
            tradeBuilderView(state, input, minute * 60000),
            toolsRefOf(state.settings),
          )
        : null,
    [source, state, version, minute, key],
  );
  const list = (ids: readonly string[]) => ids.map(encodeURIComponent).join(',');
  const path =
    leagueId === null || input === null
      ? null
      : `/leagues/${leagueId}/trades/builder?with=${encodeURIComponent(input.partnerTeamId ?? '')}` +
        `&give=${list(input.give)}&get=${list(input.get)}&drops=${list(input.myDrops)}` +
        `&givePicks=${list(input.givePicks ?? [])}&getPicks=${list(input.getPicks ?? [])}` +
        (input.counterOf ? `&counterOf=${encodeURIComponent(input.counterOf)}` : '');
  const snapshot = useLeagueView(
    path,
    TradeBuilderViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
  return input === null ? null : snapshot;
}

/** The season, one period per row (SCHEDULE.md). */
export function useSchedule(): Snapshot<ScheduleView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const simulated = useMemo(
    () => (source === 'sim' ? withholdSchedule(scheduleView(state, now)) : null),
    [source, state, version, now],
  );
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/schedule`,
    ScheduleViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
}

/**
 * The bracket: projected during the regular season, live after.
 *
 * A REAL LEAGUE READS `GET /leagues/:id/playoffs` (PLAN-2026-09-26-season-end
 * S1), from its own stored table and rounds; the simulator's bracket, which
 * the sim clock could advance independently of the real standings, is the
 * practice league's alone (AUDIT-2026-09-23 U3).
 */
export function useBracket(ask = true): Snapshot<BracketView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const simulated = useMemo(
    () => (ask && source === 'sim' ? bracketView(state, now) : null),
    [ask, source, state, version, now],
  );
  return useLeagueView(
    !ask || leagueId === null ? null : `/leagues/${leagueId}/playoffs`,
    BracketViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
}

/**
 * League awards for a period (AWARDS.md): its cards, the season's race or
 * results. The simulator's recompute every five sim seconds while live; a real
 * league reads `GET /leagues/:id/awards` (PLAN-2026-09-26-season-end S3),
 * minted when each week is settled.
 */
export function useAwardsView(period?: number): Snapshot<AwardsView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const tick = Math.floor(now / 5000);
  const simulated = useMemo(
    () => (source === 'sim' ? awardsView(state, tick * 5000, period) : null),
    [source, state, version, tick, period],
  );
  return useLeagueView(
    leagueId === null
      ? null
      : `/leagues/${leagueId}/awards${period === undefined ? '' : `?period=${period}`}`,
    AwardsViewSchema,
    simulated,
    new Date(tick * 5000).toISOString(),
  );
}

/**
 * The awards as a decoration — the chips on a final card, Home's widget, the
 * scoreboard's strip. NULL WHILE A REAL LEAGUE'S READ IS IN FLIGHT OR FAILED,
 * and its consumers draw nothing: an award chip is never worth a spinner, and
 * a failed one is said on the Awards screen, which is where it is the point.
 */
export function useAwards(period?: number): AwardsView | null {
  const snapshot = useAwardsView(period);
  return snapshot.state === 'ready'
    ? snapshot.value
    : snapshot.state === 'failed'
      ? snapshot.lastValue
      : null;
}

/**
 * One manager's trophy room. The simulator's recompute per sim minute; a real
 * league reads `GET /leagues/:id/teams/:teamId/trophies` (S3). Loading until a
 * team is chosen.
 */
export function useTrophyRoom(teamId: string | null): Snapshot<TrophyRoomView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () => (source === 'sim' && teamId ? trophyRoomView(state, teamId, minute * 60000) : null),
    [source, state, version, minute, teamId],
  );
  return useLeagueView(
    leagueId === null || teamId === null
      ? null
      : `/leagues/${leagueId}/teams/${encodeURIComponent(teamId)}/trophies`,
    TrophyRoomViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/**
 * A team's season wrapped (H9): the practice league's from the simulator, a
 * real league's from `GET /leagues/:id/teams/:teamId/wrapped`, which answers
 * `over: false` until the team's season is over.
 */
export function useSeasonWrap(teamId: string | null): Snapshot<SeasonWrapView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () => (source === 'sim' && teamId ? wrapView(state, teamId, minute * 60000) : null),
    [source, state, version, minute, teamId],
  );
  return useLeagueView(
    leagueId === null || teamId === null
      ? null
      : `/leagues/${leagueId}/teams/${encodeURIComponent(teamId)}/wrapped`,
    SeasonWrapViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/**
 * The league's yearbook (H9): the practice league's from the simulator, a
 * real league's from `GET /leagues/:id/yearbook`, which answers `over: false`
 * until the champion is crowned. `ask` false asks nothing, for the League
 * page before the playoffs.
 */
export function useYearbook(ask = true): Snapshot<YearbookView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () => (source === 'sim' && ask ? yearbookView(state, minute * 60000) : null),
    [source, state, version, minute, ask],
  );
  return useLeagueView(
    leagueId === null || !ask ? null : `/leagues/${leagueId}/yearbook`,
    YearbookViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/**
 * My championship this season once it is minted; cheap enough for the shell
 * to ask on every tick. A real league's comes from its awards read (the
 * champion is minted when the season records one, S2), whose card line
 * already carries the final's score.
 */
export function useMyChampionship(): {
  key: string;
  line: string;
  finalLine: string;
  team: {
    name: string;
    abbreviation: string;
    ownerName: string;
    colors: { primary: string; secondary: string };
    /** Its badge, whose face cheers in the moment (B-494). */
    badge?: TeamBadge;
  };
  trophyName: string;
  season: number;
  leagueName: string;
} | null {
  const { state, version } = useSim();
  const source = useLeagueSource();
  const real = useAwards();
  return useMemo(() => {
    if (source === 'api') {
      const champion = real?.season.champion ?? null;
      if (!real || !champion || !champion.team.isMine) return null;
      return {
        key: `${real.league.id}:${champion.season}`,
        line: champion.line,
        finalLine: '',
        team: {
          name: champion.team.name,
          abbreviation: champion.team.abbreviation,
          ownerName: champion.team.ownerName,
          colors: champion.team.colors,
          badge: champion.team.badge,
        },
        trophyName: champion.trophyName,
        season: champion.season,
        leagueName: real.league.name,
      };
    }
    const m = state.awards.minted.find((x) => x.awardId === 'champion' && !x.supersededBy);
    const mine = state.teams.find((t) => t.isMine);
    if (!m || !mine || !m.winners.some((w) => w.teamId === mine.id)) return null;
    return {
      key: `${state.leagueId}:${state.season}`,
      line: m.line,
      finalLine: String(m.winners[0]!.detail.finalLine ?? ''),
      team: {
        name: mine.name,
        abbreviation: mine.abbreviation,
        ownerName: mine.ownerName,
        colors: mine.colors,
      },
      trophyName: state.settings.identity.branding.trophyName,
      season: state.season,
      leagueName: state.leagueName,
    };
  }, [source, real, state, version]);
}

/**
 * The draft recap (DRAFT_RECAP.md): grades, steals, reaches. BACKLOG D6.
 *
 * ON THE API SINCE 2026-09-22, which is where `draft.finished` has been
 * pointing since D5. A simulated league still computes it here, per sim
 * minute; a real one reads `GET /api/leagues/:id/draft/recap`, where the
 * grades come from stored picks and the board they were measured against is
 * named on the artifact.
 */
export function useDraftRecap(): Snapshot<DraftRecapView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () => (source === 'sim' ? draftRecapView(state, minute * 60000) : null),
    [source, state, version, minute],
  );
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/draft/recap`,
    DraftRecapViewSchema,
    simulated,
    new Date(now).toISOString(),
  );
}

/**
 * A finished draft to play back pick by pick (C11,
 * PLAN-2026-10-31-the-draft-replay): `GET /leagues/:id/draft/replay`. A real
 * league's alone; the practice league drafts in its room on the simulator and
 * keeps no board to replay.
 */
export function useDraftReplay(): Snapshot<DraftReplayView> {
  const leagueId = useRouteLeagueId();
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/draft/replay`,
    DraftReplayViewSchema,
    null,
    new Date(0).toISOString(),
  );
}

/**
 * League history and the record book (HISTORY.md). The simulator recomputes
 * per sim minute; a real league reads `GET /leagues/:id/history`
 * (PLAN-2026-09-26-every-screen-and-crayon P3).
 */
export function useHistory(): Snapshot<HistoryView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () => (source === 'sim' ? historyView(state, minute * 60000) : null),
    [source, state, version, minute],
  );
  return useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/history`,
    HistoryViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/**
 * The news feed (PLAYER_NEWS.md), filtered, newest first: the first page, and
 * `older` to read the next. The simulator recomputes its whole feed per sim
 * minute and has no older page; a real league reads `GET /leagues/:id/news`
 * (PLAN-2026-09-26-every-screen-and-crayon P4), forty at a time.
 */
export function useNews(
  filter: NewsFilter,
  /** Only players who play one of these (B-416); empty is everyone. */
  positions: readonly string[] = [],
): {
  snapshot: Snapshot<NewsView[]>;
  older: (() => void) | null;
  loadingOlder: boolean;
} {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const wanted = positions.join(',');
  const simulated = useMemo(
    () =>
      source === 'sim'
        ? {
            items: newsItems(state, minute * 60000, filter).filter(
              (n) => wanted === '' || n.player.positions.some((p) => wanted.split(',').includes(p)),
            ),
            nextCursor: null,
          }
        : null,
    [source, state, version, minute, filter, wanted],
  );
  const query = `filter=${filter}${wanted === '' ? '' : `&positions=${encodeURIComponent(wanted)}`}`;
  const first = useLeagueView(
    leagueId === null ? null : `/leagues/${leagueId}/news?${query}`,
    NewsPageSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
  // The pages after the first, read on asking; a new first page starts over.
  const [more, setMore] = useState<{ key: unknown; items: NewsView[]; cursor: string | null }>({
    key: null,
    items: [],
    cursor: null,
  });
  const [loadingOlder, setLoadingOlder] = useState(false);
  const firstValue = first.state === 'ready' ? first.value : null;
  const extra = more.key === firstValue ? more : { key: firstValue, items: [], cursor: null };
  const cursor = extra.key === null ? null : (extra.cursor ?? firstValue?.nextCursor ?? null);
  const older =
    leagueId === null || firstValue === null || cursor === null || loadingOlder
      ? null
      : () => {
          setLoadingOlder(true);
          void get(
            `/leagues/${leagueId}/news?${query}&cursor=${encodeURIComponent(cursor)}`,
            NewsPageSchema,
          ).then((reply) => {
            setLoadingOlder(false);
            if (reply.status !== 'ok') return;
            setMore({
              key: firstValue,
              items: [...extra.items, ...reply.data.items],
              cursor: reply.data.nextCursor ?? '',
            });
          });
        };
  const snapshot = useMemo((): Snapshot<NewsView[]> => {
    if (first.state === 'ready') {
      return { ...first, value: [...first.value.items, ...extra.items] };
    }
    if (first.state === 'failed') {
      return { ...first, lastValue: first.lastValue === null ? null : first.lastValue.items };
    }
    return first;
  }, [first, extra.items]);
  return { snapshot, older: extra.cursor === '' ? null : older, loadingOlder };
}

/**
 * One player's news for the card: the first page of `GET
 * /leagues/:id/players/:playerId/news` in a real league, the simulator's in a
 * practice one. Empty with no player; null while a real league's is on its
 * way or could not be read — the card then shows no News section rather than
 * saying there is none.
 */
export function usePlayerNews(playerId: string | null): {
  items: NewsView[] | null;
  /** Reads the next page (forty to a page); null with none left or one on its way. */
  older: (() => void) | null;
} {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () =>
      source === 'sim'
        ? { items: playerId ? playerNews(state, playerId, minute * 60000) : [], nextCursor: null }
        : null,
    [source, state, version, minute, playerId],
  );
  const page = useLeagueView(
    leagueId === null || playerId === null
      ? null
      : `/leagues/${leagueId}/players/${encodeURIComponent(playerId)}/news`,
    NewsPageSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
  // ALL OF HIS NEWS, a page at a time on asking (B-414): the pages after the
  // first belong to it, and a new first page starts over.
  const first = page.state === 'ready' ? page.value : null;
  const [more, setMore] = useState<{
    key: unknown;
    items: NewsView[];
    cursor: string | null;
  } | null>(null);
  const [reading, setReading] = useState(false);
  const extra = more !== null && more.key === first ? more : null;
  const cursor = extra !== null ? extra.cursor : (first?.nextCursor ?? null);
  if (playerId === null) return { items: [], older: null };
  return {
    items: first === null ? null : [...first.items, ...(extra?.items ?? [])],
    older:
      leagueId === null || first === null || cursor === null || reading
        ? null
        : () => {
            setReading(true);
            void get(
              `/leagues/${leagueId}/players/${encodeURIComponent(playerId)}/news?cursor=${encodeURIComponent(cursor)}`,
              NewsPageSchema,
            ).then((reply) => {
              setReading(false);
              if (reply.status !== 'ok') return;
              setMore({
                key: first,
                items: [...(extra?.items ?? []), ...reply.data.items],
                cursor: reply.data.nextCursor,
              });
            });
          },
  };
}

/**
 * The weekly recap (RECAP.md): the latest settled period by default. The
 * simulator's recompute per sim minute; a real league reads
 * `GET /leagues/:id/recap` (S3).
 */
export function useRecap(period?: number): Snapshot<RecapView> {
  const { state, version, now } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const minute = Math.floor(now / 60000);
  const simulated = useMemo(
    () => (source === 'sim' ? recapView(state, minute * 60000, period) : null),
    [source, state, version, minute, period],
  );
  return useLeagueView(
    leagueId === null
      ? null
      : `/leagues/${leagueId}/recap${period === undefined ? '' : `?period=${period}`}`,
    RecapViewSchema,
    simulated,
    new Date(minute * 60000).toISOString(),
  );
}

/**
 * The league's own time zone, for waiver and lock times (B-127).
 *
 * A REAL LEAGUE'S IS ITS OWN, read from its clock (`GET …/periods`, whose
 * `timeZone` is the league's) once per league and shared (`leagueZone.ts`);
 * the simulator's settings were answering for every league until W8, so a
 * real league's clear times on the pool were told in the practice league's
 * zone. NULL WHILE IT IS BEING READ, and never a guess: a time in the wrong
 * zone is a claim, and a caller shows the time when it can say it truthfully.
 * A caller that must say why there is no time reads `useLeagueZoneState`.
 */
export function useLeagueZone(): string | null {
  return useLeagueZoneState().zone;
}

/**
 * The zone every time on a screen is told in: THE READER'S (B-181).
 *
 * Times follow the person: set on the account and changeable in its settings,
 * never worked out from the device, because a manager who travels with a
 * phone still reads a zone they chose. So:
 *
 *   * A SIGNED-IN ACCOUNT WITH A ZONE reads every time in it, in every league
 *     and on every screen, practice leagues included.
 *   * AN ACCOUNT MADE BEFORE THE ZONE COULD BE CHOSEN reads a league's times in
 *     that league's zone, as the server writes them for it — null while that
 *     zone is being read, and the caller then says no time — and a time
 *     outside any league (the hub, an invite, the league menu) in this
 *     device's, named either way, until it picks one (the hub asks). Outside a
 *     league there is no league whose zone would be the right one: the review
 *     of B-181 found the hub telling a Denver league's draft in the practice
 *     world's Central time.
 *   * A VISITOR, who has no account to hold a zone, reads this device's.
 *
 * The league's zone still decides every rule — a lock, a waiver run — and a
 * reader never sees a time in it unless they have no zone of their own.
 * `formatInZone` names the zone on every time ("Sun 11:00 AM MDT").
 */
export function useReaderZone(): string | null {
  const { session } = useSession();
  const league = useLeagueZoneState();
  const inLeague = useRouteLeagueId() !== null;
  if (session === null) return deviceZone();
  if (session.timeZone !== null) return session.timeZone;
  return inLeague ? league.zone : deviceZone();
}

/** This device's zone: only for a visitor, who has no account to hold one. */
export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** The league's zone with why it is missing, when it is, and a way to ask again. */
export function useLeagueZoneState(): LeagueZoneRead {
  const { state } = useSim();
  const leagueId = useRouteLeagueId();
  const source = useLeagueSource();
  const read = useLeagueZoneRead(source === 'api' ? leagueId : null);
  if (source === 'sim')
    return { zone: state.settings.identity.timeZone, failure: null, retry: read.retry };
  if (source === 'undecided') return { zone: null, failure: null, retry: read.retry };
  return read;
}
